import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faCoins,
  faImage,
  faPalette,
  faLeaf,
  faKey,
  faCaretDown,
  faListCheck,
  faPrint,
  faBoxesStacked,
  faCalendarDay,
  faFileInvoiceDollar,
  faTriangleExclamation,
  faHeartPulse,
  faSitemap,
  faUsers,
  faMoneyBillTransfer,
  faTags,
  faEnvelope,
  faEnvelopeCircleCheck,
  faEnvelopeOpenText,
  faRulerCombined,
} from "@fortawesome/free-solid-svg-icons";
import { enviroment } from "../../../../enviroment";
import {
  preloadRoute,
  preloadRoutes,
  whenIdle,
} from "../../../../utils/functions/routePreload";

type AdminGroup = "email";

interface AdminItem {
  to: string;
  labelKey: string;
  icon: typeof faChartLine;
  /** When false the item is filtered out (feature-flag gate). Absent = shown. */
  enabled?: boolean;
  /**
   * The group the item belongs to: its heading (a non-clickable line) is
   * drawn before the group's first VISIBLE item - so never without items.
   */
  group?: AdminGroup;
  /** A key whose text's first sentence is the item's tooltip. */
  titleKey?: string;
}

const GROUP_LABEL_KEYS: Record<AdminGroup, string> = {
  email: "headerFooter.adminGroupEmail",
};

// "Did the customer get the e-mail?" out of the page's description - the
// tooltip of the support item (no key of its own).
function firstSentence(text: string): string {
  const match = /^[^.?!]*[.?!]/.exec(text);
  return match ? match[0] : text;
}

const ADMIN_ITEMS: AdminItem[] = [
  {
    to: "/admin/statistics",
    labelKey: "headerFooter.adminStatistics",
    icon: faChartLine,
  },
  {
    to: "/admin/orders-today",
    labelKey: "headerFooter.adminOrdersToday",
    icon: faCalendarDay,
  },
  {
    to: "/admin/price-import",
    labelKey: "headerFooter.adminPriceImport",
    icon: faCoins,
  },
  {
    to: "/admin/logo-import",
    labelKey: "headerFooter.adminLogoImport",
    icon: faImage,
  },
  {
    to: "/admin/design-import",
    labelKey: "headerFooter.adminDesignImport",
    icon: faPalette,
  },
  {
    to: "/admin/import-status",
    labelKey: "headerFooter.adminImportStatus",
    icon: faTriangleExclamation,
  },
  {
    to: "/admin/system-health",
    labelKey: "headerFooter.adminSystemHealth",
    icon: faHeartPulse,
  },
  {
    to: "/admin/co2",
    labelKey: "headerFooter.adminCo2Calculator",
    icon: faLeaf,
  },
  {
    to: "/admin/orders-queue",
    labelKey: "headerFooter.adminOrdersQueue",
    icon: faListCheck,
  },
  {
    to: "/admin/prepayments",
    labelKey: "headerFooter.adminPrepayments",
    icon: faFileInvoiceDollar,
  },
  {
    to: "/admin/billing-overview",
    labelKey: "headerFooter.adminBillingOverview",
    icon: faSitemap,
    enabled: enviroment.billingOverviewEnabled,
  },
  {
    to: "/admin/vendor-users",
    labelKey: "headerFooter.adminVendorUsers",
    icon: faUsers,
    enabled: enviroment.billingOverviewEnabled,
  },
  {
    // Stripe payouts (flag REACT_APP_STRIPE_PAYOUTS_ENABLED): hidden with
    // the flag off, as are the routes.
    to: "/admin/stripe-payouts",
    labelKey: "headerFooter.adminStripePayouts",
    icon: faMoneyBillTransfer,
    enabled: enviroment.stripePayoutsEnabled,
  },
  {
    // NAV price fallback (flag REACT_APP_NAV_PRICE_FALLBACK_ADMIN_ENABLED):
    // hidden with the flag off, as is the route.
    to: "/admin/nav-price-fallback",
    labelKey: "headerFooter.adminNavPriceFallback",
    icon: faTags,
    enabled: enviroment.navPriceFallbackAdminEnabled,
  },
  {
    // Sheet size (flag REACT_APP_OVERSIZE_SHEET_ADMIN_PAGE_ENABLED): all
    // vendors with the oversize sheet guard on/off. Hidden with the flag
    // off, as is the route. Strictly `=== true`: an absent flag is OFF.
    to: "/admin/sheet-size",
    labelKey: "headerFooter.adminSheetSize",
    icon: faRulerCombined,
    enabled: enviroment.oversizeSheetAdminPageEnabled === true,
  },
  // The E-mail group: the support page first (customer service's "did the
  // customer get the e-mail?"), the log, the developers' tool last. The
  // heading "E-mail" is drawn before the first of them that is visible.
  {
    // Mail status & resend (flag REACT_APP_MAIL_DELIVERY_ADMIN_ENABLED):
    // hidden with the flag off, as is the route. Strictly `=== true`: an
    // absent flag is OFF (an untagged item would show).
    to: "/admin/mail-delivery",
    labelKey: "headerFooter.adminMailDelivery",
    titleKey: "adminScreens.mailDelivery.description",
    icon: faEnvelopeCircleCheck,
    enabled: enviroment.mailDeliveryAdminEnabled === true,
    group: "email",
  },
  {
    // Approval mail log (flag REACT_APP_APPROVAL_MAIL_STATS_ENABLED):
    // hidden with the flag off, as is the route. Strictly `=== true`: an
    // absent flag is OFF (an untagged item would show).
    to: "/admin/approval-mails",
    labelKey: "headerFooter.adminApprovalMails",
    icon: faEnvelopeOpenText,
    enabled: enviroment.approvalMailStatsEnabled === true,
    group: "email",
  },
  {
    // Mail test (technical) (flag REACT_APP_MAIL_TEST_ENABLED): hidden with
    // the flag off, as is the route.
    to: "/admin/mail-test",
    labelKey: "headerFooter.adminMailTest",
    icon: faEnvelope,
    enabled: enviroment.mailTestEnabled,
    group: "email",
  },
  {
    to: "/admin/methods",
    labelKey: "headerFooter.adminMethods",
    icon: faPrint,
  },
  {
    to: "/admin/stock-orders",
    labelKey: "headerFooter.adminStockOrders",
    icon: faBoxesStacked,
  },
  {
    to: "/admin/api-keys",
    labelKey: "headerFooter.adminApiKeys",
    icon: faKey,
  },
];

// Deliberately NOT a MUI Menu: the portal-based Menu could survive lazy-route
// navigation as a stuck overlay in the top-left corner of the screen (both the
// item-click close and a close-on-pathname-change effect failed to take effect
// while the destination chunk was loading). A plain in-flow panel positioned
// under the button cannot render anywhere else on the screen, and it unmounts
// with the header - the stuck-overlay failure mode is structurally impossible.
export function AdminMenuDropdown(): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Feature-flagged items (enabled === false) are hidden; untagged items show.
  const visibleItems = ADMIN_ITEMS.filter((item) => item.enabled !== false);

  React.useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Admin perf FE-1 (flag): the moment the dropdown opens, warm every
  // visible item's screen chunk at idle time (4-9 KB gz each, once per
  // session - the browser caches them) so the click that follows finds the
  // chunk already loaded. Fire-and-forget; a failed download is handled by
  // lazyWithRetry at the real render. Flag off = no effect, no calls.
  React.useEffect(() => {
    if (!enviroment.adminPerfRouteSuspenseEnabled || !open) return;
    // Same filter as visibleItems, read from the module constant so the
    // effect depends on `open` only.
    whenIdle(() =>
      preloadRoutes(
        ADMIN_ITEMS.filter((item) => item.enabled !== false).map(
          (item) => item.to,
        ),
      ),
    );
  }, [open]);

  // Per item: hovering/focusing a link warms exactly that route (no-op once
  // the chunk is loaded). Undefined with the flag off = no handler props.
  const preloadHandlers = enviroment.adminPerfRouteSuspenseEnabled
    ? (to: string) => ({
        onMouseEnter: () => preloadRoute(to),
        onFocus: () => preloadRoute(to),
      })
    : () => ({});

  React.useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const isActive = visibleItems.some((item) =>
    location.pathname.startsWith(item.to),
  );

  return (
    <div ref={rootRef} className="relative h-full">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`group flex items-center gap-2 px-4 h-full text-sm font-medium uppercase tracking-wide transition-colors ${
          isActive || open
            ? "text-[--primary]"
            : "text-[#596675] hover:text-[--primary]"
        }`}
      >
        <span>{t("admin")}</span>
        <FontAwesomeIcon
          icon={faCaretDown}
          className={`text-[10px] transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 w-52 rounded-md border border-slate-200 bg-white py-1 shadow-lg"
        >
          {visibleItems.map((item, index) => {
            // The group's heading before its first visible item only.
            const groupStart =
              item.group !== undefined &&
              (index === 0 || visibleItems[index - 1].group !== item.group);
            return (
              <React.Fragment key={item.to}>
                {groupStart && item.group !== undefined && (
                  <div
                    role="presentation"
                    className="px-4 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wide text-slate-500"
                    data-testid={`admin-menu-group-${item.group}`}
                  >
                    {t(GROUP_LABEL_KEYS[item.group])}
                  </div>
                )}
                <Link
                  to={item.to}
                  role="menuitem"
                  onClick={() => setOpen(false)}
                  {...preloadHandlers(item.to)}
                  title={
                    item.titleKey !== undefined
                      ? firstSentence(t(item.titleKey))
                      : undefined
                  }
                  className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-slate-50"
                  style={{
                    textDecoration: "none",
                    color: location.pathname.startsWith(item.to)
                      ? "var(--primary)"
                      : "#334155",
                  }}
                >
                  <FontAwesomeIcon
                    icon={item.icon}
                    className="w-4 text-slate-400"
                  />
                  <span>{t(item.labelKey)}</span>
                </Link>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}
